import { designVariables } from "@studio3t/design-variables";
import {
  type StageOperations,
  sourceOperations,
  stageOperations,
} from "@/pages/pipelineOperations";
import type { Connector, MiddlewareConfig, StageConfig } from "@/types/api";

export type StageType = "source" | "transform" | "sink";

// Domain aliases naming the primitive arguments below: canvas coordinates
// and spans are pixels, node ids are React Flow ids.
type Px = number;
type CardCount = number;
type NodeId = string;
type ServiceName = string;
export type ArrowDirection = "right" | "bottom";

// A `type` (not `interface`) so it satisfies React Flow's
// `Node<T extends Record<string, unknown>>` data constraint.
export type StageNodeData = {
  type: StageType;
  label: string;
  serviceName: string;
  resource: string;
  encoding?: string;
  schemaId?: string;
  /** The schema label for the card badge: the block entry's `resource`,
   *  falling back to the raw schema_id ref. In classic /studio documents
   *  `resource` is the schema's registry name; V2 Studio-emitted blocks
   *  invert the pair (`id` = name alias, `resource` = registry entry id),
   *  so this can be the entry id there. A parsed document can't tell the
   *  conventions apart — the engine accepts both. */
  schemaName?: string;
  /** The service whose store holds the stage's schema (the block entry's
   *  `service_name`): entry ids are unique only within one service, so a
   *  lookup of the schema's display name needs both. Absent when the
   *  block has no entry for the ref. */
  schemaService?: string;
  /** The stage's own rule on which operations it takes, when it has one:
   *  the source's filter, or the route a middleware or sink names. Absent
   *  when the stage takes every record. */
  operations?: StageOperations;
  unresolved: boolean;
  /** False when the stage's service isn't registered — the card has no
   *  service page to open. Undefined until the lookup answers. */
  serviceKnown?: boolean;
  /** Why the card is unresolved — /newStudio's graph strip ties the shared
   *  lint pass to the cards and lists the messages in the tooltip. */
  errors?: string[];
  // True when the card sits directly on the (dotted) canvas and needs an opaque
  // base behind its semi-transparent fill. Cards inside a grey group box don't,
  // since the group already provides the backing.
  backed: boolean;
};

export type ArrowNodeData = {
  direction: ArrowDirection;
  /** The stage the arrow leaves: the source's connector is the one the
   *  engine can report a backlog on. */
  from: StageType;
  /** The stage the arrow enters: between two transforms the engine
   *  measures no rate, which the connector says. */
  to: StageType;
};

/** The arrow node's box: it spans the whole gap between two stages along
 *  its direction and is one icon tall across it, so a connector can run
 *  edge to edge while the glyph sits centred. */
export const ARROW_BOX = {
  span: 36, // ICON_GAP + ICON_SIZE + ICON_GAP, see the geometry below
  across: 16,
};

export type StageFlowNode = {
  id: string;
  type: "stage";
  position: { x: number; y: number };
  data: StageNodeData;
};

export type ArrowFlowNode = {
  id: string;
  type: "arrow";
  position: { x: number; y: number };
  data: ArrowNodeData;
  selectable: false;
  draggable: false;
};

export type GroupFlowNode = {
  id: string;
  type: "group";
  position: { x: number; y: number };
  width: number;
  height: number;
  selectable: false;
  draggable: false;
  data: Record<string, never>;
};

export type PipelineFlowNode = StageFlowNode | ArrowFlowNode | GroupFlowNode;

export type PipelineGraph = {
  nodes: PipelineFlowNode[];
  edges: never[];
};

// Geometry — mirrors the Figma layout. Cards are a fixed 280×124
// (Bridge_PipelineStage 13984-207179 — the schema badge slot is part of
// the frame, reserved whether or not the stage refs a schema); columns and
// stacked middlewares are separated by an arrow icon (16px) with a
// semantic/spacings gap each side; groups pad by semantic/spacingl.
const CARD_W = 280;
const CARD_H = 124;
const ICON_SIZE = designVariables.primitives.spacings.a16; // 16
const ICON_GAP = designVariables.ui.semantic.spacings; // 10
const ARROW_SPAN = ICON_GAP + ICON_SIZE + ICON_GAP; // 36 — ARROW_BOX.span
const GROUP_PAD = designVariables.ui.semantic.spacingl; // 18
const MW_VGAP = CARD_H + ARROW_SPAN; // a ↓ arrow sits between stacked middlewares
const SINK_VGAP = CARD_H + designVariables.ui.semantic.spacings; // 124 + 10

const LABELS: Record<StageType, string> = {
  source: "SOURCE",
  transform: "TRANSFORM (MIDDLEWARE)",
  sink: "SINK",
};

function isUnresolved(service: ServiceName, set?: Set<string>): boolean {
  return set?.has(service) ?? false;
}

const ARROW_Y = -ICON_SIZE / 2;

type Layout = {
  mwGroup: boolean;
  sinkGroup: boolean;
  mwX: number; // left edge of the middleware column
  sinkX: number; // left edge of the sink column
  hMw: number; // middleware column height
  hSink: number; // sink column height
};

// A stacked column of N cards separated by `vgap` is 0 tall when empty.
function columnHeight(count: CardCount, vgap: Px): Px {
  return count > 0 ? (count - 1) * vgap + CARD_H : 0;
}

// Right edge of whatever precedes the sinks; null when nothing does.
function upstreamRightEdge(
  hasSource: boolean,
  mwCount: CardCount,
  mwX: Px,
  mwGroup: boolean,
): Px | null {
  if (mwCount > 0) return mwX + CARD_W + (mwGroup ? GROUP_PAD : 0);
  return hasSource ? CARD_W : null;
}

// Columns pack left to right with an arrow span between neighbours; absent
// columns (partial docs from /newStudio's graph view) take no room, so the
// first present column starts at x=0.
function computeLayout(
  hasSource: boolean,
  middlewares: MiddlewareConfig[],
  sinks: StageConfig[],
): Layout {
  const mwCount = middlewares.length;
  const sinkCount = sinks.length;
  const mwGroup = mwCount > 1;
  const sinkGroup = sinkCount > 1;
  const mwX = (hasSource ? CARD_W + ARROW_SPAN : 0) + (mwGroup ? GROUP_PAD : 0);
  const upstreamRight = upstreamRightEdge(hasSource, mwCount, mwX, mwGroup);
  const sinkX =
    (upstreamRight === null ? 0 : upstreamRight + ARROW_SPAN) +
    (sinkGroup ? GROUP_PAD : 0);
  return {
    mwGroup,
    sinkGroup,
    mwX,
    sinkX,
    hMw: columnHeight(mwCount, MW_VGAP),
    hSink: columnHeight(sinkCount, SINK_VGAP),
  };
}

// Group boxes first so they paint behind the stage cards.
function buildGroups(layout: Layout): GroupFlowNode[] {
  const groups: GroupFlowNode[] = [];
  if (layout.mwGroup) {
    groups.push({
      id: "mw-group",
      type: "group",
      position: { x: layout.mwX - GROUP_PAD, y: -layout.hMw / 2 - GROUP_PAD },
      width: CARD_W + 2 * GROUP_PAD,
      height: layout.hMw + 2 * GROUP_PAD,
      selectable: false,
      draggable: false,
      data: {},
    });
  }
  if (layout.sinkGroup) {
    groups.push({
      id: "sink-group",
      type: "group",
      position: {
        x: layout.sinkX - GROUP_PAD,
        y: -layout.hSink / 2 - GROUP_PAD,
      },
      width: CARD_W + 2 * GROUP_PAD,
      height: layout.hSink + 2 * GROUP_PAD,
      selectable: false,
      draggable: false,
      data: {},
    });
  }
  return groups;
}

function stageNode(
  id: NodeId,
  position: { x: number; y: number },
  data: StageNodeData,
): StageFlowNode {
  return { id, type: "stage", position, data };
}

type PipelineStages = {
  source?: StageConfig;
  middlewares: MiddlewareConfig[];
  sinks: StageConfig[];
};

/** What every card of one pipeline is built with: which services are
 *  unresolved, how a schema ref reads, and each stage's operations rule. */
interface CardContext {
  unresolved?: Set<string>;
  schemaNameFor: (id: string | undefined) => string | undefined;
  schemaServiceFor: (id: string | undefined) => string | undefined;
  rules: StageRules;
}

/** Each stage's rule as node data: nothing for a stage that takes every
 *  record, so a card without a rule carries no badge. */
interface StageRules {
  source: () => Pick<StageNodeData, "operations">;
  at: (
    section: "middlewares" | "sinks",
    index: number,
  ) => Pick<StageNodeData, "operations">;
}

const asNodeData = (
  operations: StageOperations,
): Pick<StageNodeData, "operations"> =>
  operations.kind === "all" ? {} : { operations };

function stageRulesOf(pipeline: Partial<Connector>): StageRules {
  return {
    source: () => asNodeData(sourceOperations(pipeline)),
    at: (section, index) =>
      asNodeData(stageOperations(pipeline, section, index)),
  };
}

/** A card's data: what every stage shows, plus its own encoding, backing
 *  and operations rule. */
function cardData(
  type: StageType,
  stage: StageConfig | MiddlewareConfig,
  ctx: CardContext,
  own: Pick<StageNodeData, "encoding" | "backed" | "operations">,
): StageNodeData {
  return {
    type,
    label: LABELS[type],
    serviceName: stage.service_name,
    resource: stage.resource,
    schemaId: stage.schema_id,
    schemaName: ctx.schemaNameFor(stage.schema_id),
    schemaService: ctx.schemaServiceFor(stage.schema_id),
    unresolved: isUnresolved(stage.service_name, ctx.unresolved),
    ...own,
  };
}

function buildStages(
  { source, middlewares, sinks }: PipelineStages,
  layout: Layout,
  ctx: CardContext,
): StageFlowNode[] {
  const stages: StageFlowNode[] = [];
  if (source) {
    const data = cardData("source", source, ctx, {
      ...ctx.rules.source(),
      encoding: source.output_encoding || "bson",
      backed: true,
    });
    stages.push(stageNode("source", { x: 0, y: -CARD_H / 2 }, data));
  }

  middlewares.forEach((m, i) => {
    const data = cardData("transform", m, ctx, {
      ...ctx.rules.at("middlewares", i),
      encoding: m.output_encoding,
      backed: !layout.mwGroup,
    });
    const y = -layout.hMw / 2 + i * MW_VGAP;
    stages.push(stageNode(`mw-${i}`, { x: layout.mwX, y }, data));
  });

  sinks.forEach((s, i) => {
    const data = cardData("sink", s, ctx, {
      ...ctx.rules.at("sinks", i),
      encoding: s.output_encoding || "bson",
      backed: !layout.sinkGroup,
    });
    const y = -layout.hSink / 2 + i * SINK_VGAP;
    stages.push(stageNode(`sink-${i}`, { x: layout.sinkX, y }, data));
  });

  return stages;
}

function horizontalArrow(
  id: NodeId,
  x: Px,
  from: StageType,
  to: StageType,
): ArrowFlowNode {
  return {
    id,
    type: "arrow",
    position: { x, y: ARROW_Y },
    data: { direction: "right", from, to },
    selectable: false,
    draggable: false,
  };
}

// Arrow nodes fill the gaps: a horizontal one spans from the upstream
// block's right edge to the next column, vertically centred; a down arrow
// spans from a middleware card's bottom edge to the next card, centred on
// the column. Arrows only connect columns that are both present.
function buildArrows(
  hasSource: boolean,
  middlewares: MiddlewareConfig[],
  sinks: StageConfig[],
  layout: Layout,
): ArrowFlowNode[] {
  const mwCount = middlewares.length;
  const sinkCount = sinks.length;
  if (mwCount === 0) {
    return hasSource && sinkCount > 0
      ? [horizontalArrow("arrow-source-sink", CARD_W, "source", "sink")]
      : [];
  }

  const arrows: ArrowFlowNode[] = hasSource
    ? [horizontalArrow("arrow-source-mw", CARD_W, "source", "transform")]
    : [];
  for (let i = 0; i < mwCount - 1; i++) {
    arrows.push({
      id: `arrow-mw-${i}`,
      type: "arrow",
      position: {
        x: layout.mwX + (CARD_W - ICON_SIZE) / 2,
        y: -layout.hMw / 2 + i * MW_VGAP + CARD_H,
      },
      data: { direction: "bottom", from: "transform", to: "transform" },
      selectable: false,
      draggable: false,
    });
  }
  if (sinkCount > 0) {
    const mwRight = layout.mwX + CARD_W + (layout.mwGroup ? GROUP_PAD : 0);
    arrows.push(horizontalArrow("arrow-mw-sink", mwRight, "transform", "sink"));
  }
  return arrows;
}

function hasStages({ source, middlewares, sinks }: PipelineStages): boolean {
  if (source) return true;
  if (middlewares.length > 0) return true;
  return sinks.length > 0;
}

export function buildPipelineGraph(
  pipeline: Partial<Connector> | null,
  unresolvedServices?: Set<string>,
): PipelineGraph {
  if (!pipeline) return { nodes: [], edges: [] };

  const { source, middlewares = [], sinks = [] } = pipeline;
  if (!hasStages({ source, middlewares, sinks })) {
    return { nodes: [], edges: [] };
  }
  const layout = computeLayout(!!source, middlewares, sinks);

  // schema_id points at a schemas-block entry; the cards show the entry's
  // `resource` (see StageNodeData.schemaName for the two conventions),
  // falling back to the raw ref.
  const schemasById = new Map(
    (pipeline.schemas ?? []).map((s) => [s.id, s] as const),
  );
  const schemaNameFor = (id: string | undefined) =>
    id === undefined ? undefined : (schemasById.get(id)?.resource ?? id);
  // The entry's service rides along: the detail page resolves the entry's
  // display name in that service's store (ids are unique only there).
  const schemaServiceFor = (id: string | undefined) =>
    id === undefined ? undefined : schemasById.get(id)?.service_name;

  return {
    nodes: [
      ...buildGroups(layout),
      ...buildStages({ source, middlewares, sinks }, layout, {
        unresolved: unresolvedServices,
        schemaNameFor,
        schemaServiceFor,
        rules: stageRulesOf(pipeline),
      }),
      ...buildArrows(!!source, middlewares, sinks, layout),
    ],
    edges: [],
  };
}
